import { useCallback, useEffect, useState } from 'react';
import { useVault } from '../lib/vault';
import { api } from '../lib/api';
import { plural } from '@shared/text';
import {
  KIND_LABEL,
  ONBOARDING_HINT,
  ONBOARDING_LABEL,
  type OnboardingPlan,
} from '@shared/types';
import { Confirm, ErrorNote, useAction } from '../components/ui';

function PlanCard({
  plan,
  onChange,
}: {
  plan: OnboardingPlan;
  onChange: (plan: OnboardingPlan) => void;
}): JSX.Element {
  const { busy, error, run } = useAction();
  const done = plan.entries.filter((e) => e.done).length;
  const total = plan.entries.length;
  const progress = total === 0 ? 100 : Math.round((done / total) * 100);
  const offen = plan.entries.filter((e) => !e.done);

  return (
    <div className="card">
      <div className="card-title">
        <div>
          <h2>{plan.name}</h2>
          <p className="small muted" style={{ margin: '3px 0 0' }}>
            {plan.email} · begonnen am {new Date(plan.startedAt).toLocaleDateString('de-DE')}
          </p>
        </div>
        {plan.completedAt ? (
          <span className="badge badge-ok">
            fertig eingerichtet am {new Date(plan.completedAt).toLocaleDateString('de-DE')}
          </span>
        ) : (
          <span className={progress === 100 ? 'badge badge-ok' : 'badge badge-warn'}>
            {done} von {total} erledigt
          </span>
        )}
      </div>

      <ErrorNote error={error} />

      <div className="bar-track" style={{ marginBottom: 14 }}>
        <div
          className="bar-fill"
          style={{ width: `${progress}%`, background: progress === 100 ? 'var(--ok)' : undefined }}
        />
      </div>

      <table style={{ marginTop: 14 }}>
        <thead>
          <tr>
            <th style={{ width: 34 }} />
            <th>Was zu tun ist</th>
            <th>Wo</th>
          </tr>
        </thead>
        <tbody>
          {plan.entries.map((entry) => (
            <tr key={entry.itemId}>
              <td>
                <input
                  type="checkbox"
                  checked={entry.done}
                  disabled={busy || Boolean(plan.completedAt)}
                  aria-label={`${entry.title} erledigt`}
                  style={{ width: 'auto' }}
                  onChange={async (e) => {
                    const next = await run(() =>
                      api.checkOnboardingEntry(plan.uid, entry.itemId, e.target.checked),
                    );
                    if (next) onChange(next);
                  }}
                />
              </td>
              <td>
                <div className="row-tight">
                  <strong style={{ textDecoration: entry.done ? 'line-through' : undefined }}>
                    {ONBOARDING_LABEL[entry.action]}
                  </strong>
                  {entry.criticality === 'high' ? (
                    <span className="badge badge-danger">zuerst</span>
                  ) : null}
                </div>
                <div className="small muted">{ONBOARDING_HINT[entry.action]}</div>
              </td>
              <td>
                <div className="small">
                  <strong>{entry.title}</strong>
                </div>
                <div className="small muted">
                  {[KIND_LABEL[entry.kind], entry.category].filter(Boolean).join(' · ')}
                </div>
                <div className="row-tight">
                  {entry.url ? (
                    <a className="small" href={entry.url} target="_blank" rel="noreferrer noopener">
                      {entry.url.replace(/^https?:\/\//, '')}{'\u00a0↗'}
                    </a>
                  ) : null}
                  {/* Die Anleitung steht hier bewusst gleich daneben: beim
                      Einrichten wird sie gebraucht, nicht drei Klicks später. */}
                  {entry.guideUrl ? (
                    <a
                      className="small"
                      href={entry.guideUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      📄 {entry.guideTitle || 'Anleitung'}{'\u00a0↗'}
                    </a>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!plan.completedAt ? (
        <div className="row" style={{ marginTop: 14 }}>
          <span className="spacer" />
          <button
            className="btn-primary"
            disabled={busy || offen.length > 0}
            title={offen.length > 0 ? 'Erst alle Punkte abhaken' : undefined}
            onClick={async () => {
              const next = await run(() => api.completeOnboarding(plan.uid));
              if (next) onChange(next);
            }}
          >
            Onboarding abschließen
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * `startFor` kommt von der Mannschaftsliste: dort steht die Person schon fest,
 * also soll man sie hier nicht noch einmal aus einer Liste suchen muessen.
 */
export function OnboardingPage({ startFor = null }: { startFor?: string | null } = {}): JSX.Element {
  const { users, me, items } = useVault();
  const [plans, setPlans] = useState<OnboardingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [startUid, setStartUid] = useState(startFor ?? '');
  const [confirmStart, setConfirmStart] = useState(false);

  useEffect(() => {
    if (startFor) setStartUid(startFor);
  }, [startFor]);
  const { busy, error, setError, run } = useAction();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPlans(await api.onboardingPlans());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    void load();
  }, [load]);

  const grundausstattung = items.filter((i) => i.onboardingRequired && i.status !== 'cancelled');
  const candidates = users.filter((u) => u.uid !== me?.uid && u.status === 'active');
  const target = users.find((u) => u.uid === startUid);
  const open = plans.filter((p) => !p.completedAt);
  const closed = plans.filter((p) => p.completedAt);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Onboarding</h1>
        </div>
      </div>

      <ErrorNote error={error} />

      {grundausstattung.length === 0 ? (
        <div className="note note-warn">
          <strong>Noch kein Zugang gehört zur Grundausstattung.</strong> Setz bei den Einträgen,
          die neue Leute brauchen, den Haken <em>„Neue Leute brauchen diesen Zugang"</em> – erst
          dann kann hier eine Liste entstehen.
        </div>
      ) : (
        <div className="card">
          <div className="card-title">
            <h2>Jemanden einrichten</h2>
            <span className="small muted">
              {plural(grundausstattung.length, 'Zugang gehört', 'Zugänge gehören')} zur
              Grundausstattung
            </span>
          </div>
          <div className="row">
            <select
              value={startUid}
              onChange={(e) => setStartUid(e.target.value)}
              style={{ maxWidth: 320 }}
            >
              <option value="">Person auswählen …</option>
              {candidates.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
            <button
              className="btn-primary"
              disabled={!startUid || busy}
              onClick={() => setConfirmStart(true)}
            >
              Onboarding starten
            </button>
          </div>
          {candidates.length === 0 ? (
            <p className="small muted" style={{ marginTop: 10 }}>
              Die Person muss sich einmal selbst angemeldet haben, dann taucht sie hier auf.
            </p>
          ) : null}
        </div>
      )}

      {loading ? (
        <div className="empty">Wird geladen …</div>
      ) : (
        <>
          {open.length > 0 ? (
            <div style={{ marginTop: 14 }}>
              <h2 style={{ marginBottom: 10 }}>Laufend</h2>
              <div className="stack">
                {open.map((plan) => (
                  <PlanCard
                    key={plan.uid}
                    plan={plan}
                    onChange={(next) =>
                      setPlans((prev) => prev.map((p) => (p.uid === next.uid ? next : p)))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          {closed.length > 0 ? (
            <div style={{ marginTop: 22 }}>
              <h2 style={{ marginBottom: 10 }}>Abgeschlossen</h2>
              <div className="stack">
                {closed.map((plan) => (
                  <PlanCard
                    key={plan.uid}
                    plan={plan}
                    onChange={(next) =>
                      setPlans((prev) => prev.map((p) => (p.uid === next.uid ? next : p)))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          {plans.length === 0 && grundausstattung.length > 0 ? (
            <div className="empty">Noch niemand eingerichtet.</div>
          ) : null}
        </>
      )}

      {confirmStart && target ? (
        <Confirm
          title={`Onboarding für ${target.name}?`}
          confirmLabel="Starten"
          message={
            <>
              <p>
                Es entsteht eine Liste mit{' '}
                {plural(grundausstattung.length, 'Punkt', 'Punkten')} – allem, was als
                Grundausstattung markiert ist.
              </p>
              <p className="muted">
                Freigeben und Plätze zuweisen musst du weiterhin selbst; die Liste hakt sich dann
                von allein ab.
              </p>
            </>
          }
          onConfirm={async () => {
            const plan = await api.startOnboarding(target.uid);
            setPlans((prev) => [plan, ...prev.filter((p) => p.uid !== plan.uid)]);
            setStartUid('');
          }}
          onClose={() => setConfirmStart(false)}
        />
      ) : null}
    </>
  );
}
